import {QuestionCustomProperties} from "../models/definitions/QuestionCustomProperties";
import {QuestionSetup} from "../models/definitions/QuestionSetup";
import {QuestionDefaultPositioning} from "../models/definitions/QuestionDefaultPositioning";
import {QuestionValueType} from "../models/definitions/QuestionValueType";
import {QuestionQueryType} from "../models/definitions/QuestionQueryType";
import {ComposedQuestion} from "../models/definitions/ComposedQuestion";
import {QuestionFrontEndName} from "../models/definitions/QuestionFrontEndName";
import {QuestionClientName} from "../models/definitions/QuestionClientName";
import {LiteralExpression} from "../models/expressions/LiteralExpression";

const defaultMissingRequiredValueErrorMessage = "Dit veld is verplicht om in te vullen.";

export class QuestionComposer {
	composeQuestion(setups: QuestionSetup[], frontEnd: QuestionFrontEndName, client: QuestionClientName): ComposedQuestion | null {
		const usedSetups = setups.filter(it =>
			(it.frontEndName === undefined || it.frontEndName === null || it.frontEndName === frontEnd)
			&& (it.clientName === undefined || it.clientName === null || it.clientName === client)
		);
		
		if (usedSetups.length === 0)
			return null;
		
		const questionDefinition = setups[0].definition;
		if (questionDefinition === null || !usedSetups.some(it => it.definition === questionDefinition))
			return null;
		
		console.log("composing questions from", setups, frontEnd, client, questionDefinition);
		
		const chain = QuestionComposer.loadQuestionChain(usedSetups, questionDefinition, frontEnd, client);
		console.log(chain);
		if (chain === null)
			return null;
		
		const combinedSetup = QuestionComposer.combineQuestionsList(chain);
		return this.createQuestion(combinedSetup);
	}
	
	private static loadQuestionChain(setups: QuestionSetup[], definition: string, frontEnd: QuestionFrontEndName, client: QuestionClientName): QuestionSetup[] | null {
		const index = new Map<string, QuestionSetup>();
		setups.forEach(it => index.set(it.fullId, it));
		
		const stack: string[] = [];
		let id: string | null = definition;
		const chain: QuestionSetup[] = [];
		while (id !== null && id !== undefined) {
			const fullIds = [
				`${id}:${frontEnd}:${client}`,
				`${id}:*:${client}`,
				`${id}:${frontEnd}:*`,
				`${id}:*:*`,
			];
			stack.push(id);
			id = null;
			
			fullIds
				.filter(it => index.has(it))
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				.map(it => index.get(it)!)
				// eslint-disable-next-line no-loop-func
				.forEach(it => {
					if (it.extends)
						id = it.extends;
					chain.unshift(it);
				});
			
			if (id !== null && stack.indexOf(id) >= 0) {
				// todo tell user that there is a cyclic extends reference
				return null;
			}
		}
		
		return chain;
	}

	private createQuestion(setup: QuestionSetup): ComposedQuestion {
		return {
			name: setup.name ?? setup.definition,
			label: setup.label?.value ?? "",
			subLabel: setup.subLabel?.value ?? "",
			labelExpression: setup.labelExpression?.value ?? null,
			subLabelExpression: setup.subLabelExpression?.value ?? null,
			description: setup.description?.value ?? null,
			extraDisplay: setup.extraDisplay?.value ?? null,
			valueType: setup.valueType?.value ?? "string",
			queryType: setup.queryType?.value ?? QuestionComposer.deriveQueryType(setup.valueType?.value),
			valueOptions: setup.valueOptions?.value ?? null,
			valueMapping: setup.valueMapping?.value ?? null,
			unit: setup.unit?.value ?? null,
			missingRequiredValueErrorMessage: setup.missingRequiredValueErrorMessage?.value ?? defaultMissingRequiredValueErrorMessage,
			defaultValue: setup.defaultValue?.value ?? null,
			defaultValueExpression: setup.defaultValueExpression?.value ?? null,
			visibleExpression: setup.visibleExpression?.value ?? QuestionComposer.defaultVisibleExpression(),
			enabledExpression: setup.enabledExpression?.value ?? QuestionComposer.defaultEnabledExpression(),
			editableExpression: setup.editableExpression?.value ?? QuestionComposer.defaultEditableExpression(),
			requiredExpression: setup.requiredExpression?.value ?? QuestionComposer.defaultRequiredExpression(),
			validators: setup.validators ?? [],
			mapping: setup.mapping?.value ?? [],
			defaultPositioning: setup.defaultPositioning?.value ?? {} as QuestionDefaultPositioning,
			parameters: setup.parameters ?? [],
			value: null,
			placeholder: setup.placeholder?.value ?? null,
			inputMask: setup.inputMask?.value ?? null,
			custom: setup.custom,
		};
	}
	
	private static defaultVisibleExpression(): LiteralExpression {
		return {
			function: "literal",
			value: "true",
		};
	}
	
	private static defaultEnabledExpression(): LiteralExpression {
		return {
			function: "literal",
			value: "true",
		};
	}
	
	private static defaultEditableExpression(): LiteralExpression {
		return {
			function: "literal",
			value: "true",
		};
	}
	
	private static defaultRequiredExpression(): LiteralExpression {
		return {
			function: "literal",
			value: "true",
		};
	}
	
	private static deriveQueryType(valueType: QuestionValueType | null | undefined): QuestionQueryType {
		switch (valueType) {
			case "string":
				return "string";
			case "decimal":
				return "decimal";
			case "integer":
				return "integer";
			case "boolean":
				return "boolean";
			case "uuid":
				return "string";
			case "date":
				return "string";
			case "time":
				return "string";
			case "datetime":
				return "string";
			case "email":
				return "string";
			case "iban":
				return "string";
			case "licensePlateNld":
				return "string";
			case "postalcodeNld":
				return "string";
			case "phoneNumber":
				return "string";
			default:
				return "string";
		}
	}
	
	private static combineQuestionsList(setups: QuestionSetup[]): QuestionSetup {
		return setups.reduce(QuestionComposer.combineQuestions);
	}

	private static combineQuestions(base: QuestionSetup, override: QuestionSetup): QuestionSetup {
		return {
			fullId: override.fullId,
			definition: override.definition,
			"extends": override.extends,
			frontEndName: override.frontEndName,
			clientName: override.clientName,

			name: override.name,
			description: override.description ?? base.description,
			extraDisplay: override.extraDisplay ?? base.extraDisplay,
			label: override.label ?? base.label,
			subLabel: override.subLabel ?? base.subLabel,
			labelExpression: override.labelExpression ?? base.labelExpression,
			subLabelExpression: override.subLabelExpression ?? base.subLabelExpression,
			defaultValue: override.defaultValue ?? base.defaultValue,
			valueType: override.valueType ?? base.valueType,
			queryType: override.queryType ?? base.queryType,
			valueOptions: override.valueOptions ?? base.valueOptions,
			mapping: override.mapping ?? base.mapping,
			valueMapping: override.valueMapping ?? base.valueMapping,
			unit: override.unit ?? base.unit,
			missingRequiredValueErrorMessage: override.missingRequiredValueErrorMessage ?? base.missingRequiredValueErrorMessage,
			defaultValueExpression: override.defaultValueExpression ?? base.defaultValueExpression,
			visibleExpression: override.visibleExpression ?? base.visibleExpression,
			enabledExpression: override.enabledExpression ?? base.enabledExpression,
			editableExpression: override.editableExpression ?? base.editableExpression,
			requiredExpression: override.requiredExpression ?? base.requiredExpression,
			validators: override.validators ?? base.validators,
			defaultPositioning: override.defaultPositioning ?? base.defaultPositioning,
			parameters: QuestionComposer.combineParameters(base.parameters, override.parameters),
			placeholder: override.placeholder ?? base.placeholder,
			inputMask: override.inputMask ?? base.inputMask,
			custom: QuestionComposer.combineCustomProperties(base.custom, override.custom),
		};
	}
	
	private static combineParameters(base: string[] | null, override: string[] | null): string[] {
		if (base == null)
			return override ?? [];
		if (override == null)
			return base ?? [];
		return base.concat(override);
	}
	
	private static combineCustomProperties(base: QuestionCustomProperties | null, override: QuestionCustomProperties | null): QuestionCustomProperties {
		return {
			postback: override?.postback ?? base?.postback ?? null,
		};
	}
}

